/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2018
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define(['underscore'], (_) => {


	class ConditionalPaletteUpgradeHelper {
		/**
		 * Perform upgrade
		 *
		 * @param {object} spec - spec to perform upgrade on
		 *
		 * @return {Promise} Promise to be resolved when upgrade performed
		 */
		upgrade(spec, dashboardApi) {
			return dashboardApi.getGlassSvc('.DashboardTheme').then((dashboardThemeService) => {
				return dashboardThemeService.getThemeDefinition(spec.theme).then((themeDefinition) => {
					// Get all the conditional palettes for the theme.
					const conditionalPalettes = themeDefinition.getPalettes('ConditionalPalette');

					for (let widgetId in spec.widgets) {
						const widget = spec.widgets[widgetId];
						this._updateWidgetConditionalPalette(widget, conditionalPalettes, dashboardApi);
					}

					return spec;
				});
			});
		}

		/**
		 * If the widget has a conditional palette, get the palette name by comparing the hex values that are saved in the spec
		 */
		_updateWidgetConditionalPalette(widget, conditionalPalettes, dashboardApi) {
			if (!widget || !widget.conditions || !widget.conditions.palette) {
				return;
			}

			const widgetPaletteColors = widget.conditions.palette.colors;
			// If we already have an Endor spec
			if (!widgetPaletteColors || widgetPaletteColors.length < 1 || !widgetPaletteColors[0].bgcolor) {
				return;
			}

			const paletteColors = widgetPaletteColors.map(
				({ bgcolor }) => bgcolor	//pluck bgcolor
			).filter(
				color => color && color.length	//filter out invalid colours
			).map(
				color => color.toUpperCase()	//to allow case-insensitive search
			);

			const isWidgetPaletteAPattern = widgetPaletteColors[0].pattern ? true : false;
			let paletteId = null;
			for (let conditionalPalette of conditionalPalettes) {
				if (isWidgetPaletteAPattern) {
					if (conditionalPalette.fills[0].pattern) {
						paletteId = conditionalPalette.id;
						break;
					}
				} else {
					let foundAllColors = true;

					const conditionalPaletteColours = conditionalPalette.fills.filter(
						fill => _.isString(fill)	//we only attempt to match against strings
					).map(
						fill => fill.toUpperCase()	//to allow case-insensitive search
					);

					// Since the user could of removed any number of colors from the palette we can't simply do a direct match.
					// Loops through all the colors from the widget spec and see if they're all found in the palette.  If they
					// are concider it a match
					for (let color of paletteColors) {
						if (conditionalPaletteColours.indexOf(color) === -1) {
							foundAllColors = false;
							break;
						}
					}

					if (foundAllColors) {
						paletteId = conditionalPalette.id;
						break;
					}
				}
			}

			// Should never happen, we should always find a match.  But log it as an error if we don't
			if (!paletteId) {
				dashboardApi.getGlassCoreSvc('.Logger').error('Could not find a match for the conditional palette', widget);
			}

			// Make sure we have a properties array
			if (!widget.properties) {
				widget.properties = [];
			}

			widget.properties.push({ id: 'condColorPalette', value: paletteId });
		}
	}

	return ConditionalPaletteUpgradeHelper;
});
